<template>
  <div class="rv-requests">
    <!-- Going-out-next checkpoint. Requests sit 'scheduled' for hours before they fire
         (scheduled send time), which is the office's window to veto one — but only if they know
         it exists. This turns that passive gap into a visible prompt without making
         anything mandatory: automation still runs if nobody looks. -->
    <div v-if="pendingSend > 0 && statusFilter !== 'scheduled'" class="rv-pending-banner" @click="showScheduled">
      <div class="rv-pending-icon"><i class="fa fa-paper-plane"></i></div>
      <div class="rv-pending-text">
        <b>{{ pendingSend }} review {{ pendingSend === 1 ? 'request goes' : 'requests go' }} out {{ nextSendLabel }}.</b>
        Had a visit that didn't go well? Review the list and cancel any you'd rather not send.
      </div>
      <button type="button" class="rv-pending-btn">Review<i class="fa fa-chevron-right ml-1"></i></button>
    </div>

    <!-- Stat cards -->
    <div class="rv-stat-grid">
      <div class="rv-stat-card" v-for="card in statCards" :key="card.label">
        <div class="rv-stat-label">{{ card.label }}</div>
        <div class="rv-stat-value">{{ card.value }}</div>
        <div class="rv-stat-sub">{{ card.sub }}</div>
      </div>
    </div>

    <!-- Section head + status filter -->
    <div class="rv-section-head">
      <div>
        <h2 class="rv-h2">Recent appointments</h2>
        <div class="rv-h2-sub">Requests fire automatically after each completed visit. Toggle a patient off if you'd rather not ask.</div>
      </div>
      <div class="rv-filter">
        <label class="rv-filter-label">Status</label>
        <select class="rv-select" v-model="statusFilter" @change="onFilterChange">
          <option value="">All statuses</option>
          <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
      </div>
    </div>

    <!-- Table -->
    <div class="rv-card rv-table-card">
      <div class="rv-table-head">
        <span>Patient</span>
        <span>Appointment</span>
        <span>Channel</span>
        <span>Request status</span>
        <span>Follow-up</span>
        <span class="rv-right">Send requests</span>
      </div>

      <div v-if="loading" class="rv-table-empty">
        <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
      </div>

      <div v-else-if="!requests.length" class="rv-table-empty">
        <i class="fa fa-inbox"></i>
        <span>No review requests yet{{ statusFilter ? ' for this status' : '' }}.</span>
      </div>

      <div
        v-else
        v-for="row in requests"
        :key="row.requestID"
        class="rv-row"
        :class="{ 'is-off': row.optedOut }"
      >
        <!-- Patient -->
        <div class="rv-cell rv-patient">
          <div class="rv-avatar">{{ initials(row.patientName) }}</div>
          <div class="rv-patient-meta">
            <div class="rv-name">{{ row.patientName || 'Unknown patient' }}</div>
            <div class="rv-pid">#{{ row.patNum }}</div>
          </div>
        </div>

        <!-- Appointment -->
        <div class="rv-cell">
          <div class="rv-appt">{{ formatAppt(row.aptDateTime) }}</div>
          <div class="rv-appt-sub">{{ row.source === 'manual' ? 'Manual send' : 'Auto after visit' }}</div>
        </div>

        <!-- Channel -->
        <div class="rv-cell rv-channel">
          <span v-if="row.channels.sms" class="rv-chan"><i class="fa fa-comment-o"></i>SMS</span>
          <span v-if="row.channels.email" class="rv-chan"><i class="fa fa-envelope-o"></i>Email</span>
          <span v-if="!row.channels.sms && !row.channels.email" class="rv-chan rv-chan-none">—</span>
        </div>

        <!-- Status -->
        <div class="rv-cell">
          <span class="rv-status-dot" :class="'is-' + statusMeta(row.status).kind">
            <span class="rv-dot"></span>{{ statusMeta(row.status).label }}
          </span>
          <div class="rv-status-when" v-if="row.sentOn">{{ formatAppt(row.sentOn) }}</div>
          <div
            v-if="(row.status === 'skipped' || row.status === 'cancelled') && row.skipReason"
            class="rv-skip-reason"
            data-test="request-skip-reason"
          >{{ skipReasonLabel(row.skipReason) }}</div>
        </div>

        <!-- Follow-up -->
        <div class="rv-cell rv-followup">{{ followUpLabel(row) }}</div>

        <!-- Per-visit veto + the permanent per-patient switch. Deliberately adjacent and
             deliberately different weights: Cancel drops THIS visit's request and the
             patient stays in the programme, the switch stops every future one. -->
        <div class="rv-cell rv-right rv-actions">
          <button
            v-if="row.status === 'scheduled'"
            type="button"
            class="rv-cancel-btn"
            :disabled="cancellingIDs.includes(row.requestID)"
            title="Don't send this one — the patient stays opted in for future visits"
            @click="cancelRequest(row)"
          >
            {{ cancellingIDs.includes(row.requestID) ? 'Cancelling…' : 'Cancel' }}
          </button>
          <label class="rv-switch" :title="row.optedOut ? 'On the Do not send list — requests paused' : 'Requests on'" data-test="request-optout-switch">
            <input
              type="checkbox"
              :checked="!row.optedOut"
              :disabled="togglingPatNums.includes(row.patNum)"
              @change="toggleOptOut(row, $event)"
            />
            <span class="rv-switch-track"><span class="rv-switch-thumb"></span></span>
          </label>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="rv-pager" v-if="total > pageSize">
      <button class="rv-pager-btn" :disabled="offset === 0 || loading" @click="prevPage">
        <i class="fa fa-chevron-left"></i> Prev
      </button>
      <span class="rv-pager-info">{{ rangeStart }}–{{ rangeEnd }} of {{ total }}</span>
      <button class="rv-pager-btn" :disabled="offset + pageSize >= total || loading" @click="nextPage">
        Next <i class="fa fa-chevron-right"></i>
      </button>
    </div>

    <!-- Turning a row's switch OFF adds the patient to the Do not send list with a reason. -->
    <DoNotSendModal
      :show="dnsModal.open"
      mode="add"
      :patient="dnsModal.patient"
      @saved="onDoNotSendSaved"
      @close="dnsModal.open = false"
    />
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import mixin from '../../../mixin.js';
import DoNotSendModal from './DoNotSendModal.vue';
import { skipReasonLabel, errorForLog } from '../doNotSend';

const STATUS_META = {
  scheduled:     { kind: 'neutral', label: 'Scheduled' },
  sent:          { kind: 'blue',    label: 'Sent' },
  clicked:       { kind: 'teal',    label: 'Opened' },
  rated_public:  { kind: 'green',   label: 'Clicked Google' },
  rated_private: { kind: 'amber',   label: 'Private feedback' },
  reviewed:      { kind: 'green',   label: 'Reviewed' },
  opted_out:     { kind: 'gray',    label: 'Opted out' },
  cancelled:     { kind: 'gray',    label: 'Cancelled' },
  skipped:       { kind: 'gray',    label: 'Skipped' },
  failed:        { kind: 'red',     label: 'Failed' },
};

const VALID_STATUSES = Object.keys(STATUS_META);

/**
 * Seed the status filter from ?status= so the Overview's "going out next" banner can deep-link
 * straight to the cancellable rows. Whitelisted against the known statuses so a stray query
 * param can't send an arbitrary string to the API, and tolerant of a missing $route so the
 * component can be mounted without a router.
 */
function initialStatusFilter($route) {
  const s = $route && $route.query && $route.query.status;
  return VALID_STATUSES.includes(s) ? s : '';
}

export default {
  name: 'ReviewsRequests',
  components: { DoNotSendModal },
  mixins: [mixin],

  data() {
    return {
      statusFilter: initialStatusFilter(this.$route),
      offset: 0,
      pageSize: 50,
      togglingPatNums: [],
      cancellingIDs: [],
      dnsModal: { open: false, patient: null },
      statusOptions: [
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'sent', label: 'Sent' },
        { value: 'clicked', label: 'Opened' },
        { value: 'rated_public', label: 'Clicked Google' },
        { value: 'rated_private', label: 'Private feedback' },
        { value: 'opted_out', label: 'Opted out' },
        { value: 'skipped', label: 'Skipped' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'failed', label: 'Failed' },
      ],
    };
  },

  async created() {
    await Promise.all([this.loadDashboard(), this.loadRequests()]);
  },

  methods: {
    ...mapActions('reviews', ['getReviewsDashboard', 'getReviewRequests', 'setReviewPatientOptout', 'cancelReviewRequest']),
    ...mapMutations('reviews', ['PATCH_REQUEST_OPTOUT']),

    // Cancel ONE pending request. Distinct from the opt-out switch beside it: the patient
    // keeps receiving requests after future visits, and because 'cancelled' is excluded
    // from the server's 7-day resend guard, staff can still send this patient manually
    // today if they change their mind.
    async cancelRequest(row) {
      if (this.cancellingIDs.includes(row.requestID)) return;
      const who = row.patientName || 'this patient';
      if (!window.confirm(`Don't send the review request to ${who} for this visit?\n\nThey'll still be asked after future appointments — use the switch instead to stop all requests for this patient.`)) {
        return;
      }
      this.cancellingIDs.push(row.requestID);
      try {
        await this.cancelReviewRequest({ siteID: this.siteID, requestID: row.requestID });
        this.$toast && this.$toast.success('Review request cancelled.');
        // Reload rather than patch locally: the row's status drives which actions render,
        // and the Overview's pending count is now stale too.
        await Promise.all([this.loadRequests(), this.getReviewsDashboard({ siteID: this.siteID, rangeDays: 30 })]);
      } catch (err) {
        console.error('[ReviewsRequests] cancel failed:', err);
        // A 409 here is the honest answer to a real race (it already sent, or a colleague
        // cancelled it first) — surface the server's sentence rather than a generic error.
        const data = err && err.response && err.response.data;
        const msg = (data && (data.message || (typeof data === 'string' ? data : null)))
          || 'Could not cancel that request.';
        this.$toast && this.$toast.error(msg);
        await this.loadRequests();
      } finally {
        this.cancellingIDs = this.cancellingIDs.filter((id) => id !== row.requestID);
      }
    },

    async loadDashboard() {
      try {
        if (!this.dashboard) await this.getReviewsDashboard({ siteID: this.siteID, rangeDays: 30 });
      } catch (err) {
        console.error('[ReviewsRequests] dashboard load failed:', err);
      }
    },

    async loadRequests() {
      try {
        await this.getReviewRequests({
          siteID: this.siteID,
          limit: this.pageSize,
          offset: this.offset,
          status: this.statusFilter || undefined,
        });
      } catch (err) {
        console.error('[ReviewsRequests] requests load failed:', err);
        this.$toast && this.$toast.error('Could not load review requests.');
      }
    },

    onFilterChange() {
      this.offset = 0;
      this.loadRequests();
    },

    // The banner's action: filter to the only rows that can still be cancelled. No
    // navigation — the list is already on this tab, so this is a filter, not a jump.
    showScheduled() {
      this.statusFilter = 'scheduled';
      this.onFilterChange();
    },

    nextPage() {
      if (this.offset + this.pageSize < this.total) {
        this.offset += this.pageSize;
        this.loadRequests();
      }
    },

    prevPage() {
      if (this.offset > 0) {
        this.offset = Math.max(0, this.offset - this.pageSize);
        this.loadRequests();
      }
    },

    // Switch OFF → the Do not send modal (reason, note, how long). Switch ON → confirm, then take
    // the patient off the list with an optimistic flip that rolls back on failure.
    async toggleOptOut(row, e) {
      const patNum = row.patNum;
      // The checkbox is bound one-way (:checked="!row.optedOut"). Put the DOM back until the
      // server agrees — otherwise cancelling the modal leaves the switch showing OFF for a
      // patient who is still being asked.
      if (e && e.target) e.target.checked = !row.optedOut;
      if (this.togglingPatNums.includes(patNum)) return;

      if (!row.optedOut) {
        this.dnsModal = { open: true, patient: { patNum, patientName: row.patientName || '' } };
        return;
      }

      const who = row.patientName || 'this patient';
      if (!window.confirm(`Remove ${who} from the Do not send list?\n\nThey can be asked for a review again after future visits.`)) {
        return;
      }

      this.togglingPatNums.push(patNum);
      this.patchPatientRows(patNum, false);
      try {
        await this.setReviewPatientOptout({
          siteID: this.siteID,
          patNum,
          optedOut: false,
          employeeID: this.user && this.user.EmployeeID,
        });
        this.$toast && this.$toast.success('Review requests re-enabled.');
        this.getReviewsDashboard({ siteID: this.siteID, rangeDays: 30 }).catch(() => {});
      } catch (err) {
        console.error('[ReviewsRequests] opt-out toggle failed:', errorForLog(err));
        this.patchPatientRows(patNum, true);
        this.$toast && this.$toast.error('Could not update this patient.');
      } finally {
        this.togglingPatNums = this.togglingPatNums.filter((p) => p !== patNum);
      }
    },

    // Every row for the patient flips together — the list is patient-wide, not per visit.
    patchPatientRows(patNum, optedOut) {
      this.requests
        .filter((r) => r.patNum === patNum)
        .forEach((r) => this.PATCH_REQUEST_OPTOUT({ requestID: r.requestID, optedOut }));
    },

    async onDoNotSendSaved(entry, meta) {
      const patNum = meta && meta.patNum;
      if (patNum !== undefined && patNum !== null) this.patchPatientRows(patNum, true);
      const who = (meta && meta.patientName) || 'This patient';
      this.$toast && this.$toast.success(`${who} was added to the Do not send list.`);
      // Reload: the server flipped this patient's still-scheduled rows to opted_out, and the
      // "Do not send" stat card is now one higher.
      await Promise.all([
        this.loadRequests(),
        this.getReviewsDashboard({ siteID: this.siteID, rangeDays: 30 }).catch(() => {}),
      ]);
    },

    skipReasonLabel(code) {
      return skipReasonLabel(code);
    },

    statusMeta(status) {
      return STATUS_META[status] || { kind: 'neutral', label: status };
    },

    initials(name) {
      if (!name) return '?';
      return name.split(/\s+/).filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join('');
    },

    formatAppt(value) {
      if (!value) return '—';
      // AptDateTime / SentOn are stored as site-TZ wall-clock — interpret the
      // string in the site zone (moment.tz parse-in-zone, no double-conversion).
      const m = this.timezone ? moment.tz(value, this.timezone) : moment(value);
      return m.isValid() ? m.format('MMM D, YYYY h:mm A') : '—';
    },

    followUpLabel(row) {
      if (row.nextFollowUpAt) return `Next: ${this.formatAppt(row.nextFollowUpAt)}`;
      if (row.followUpCount > 0) return `${row.followUpCount} sent`;
      return 'None scheduled';
    },
  },

  computed: {
    ...mapState('reviews', {
      requests: (s) => s.requests,
      total: (s) => s.requestsTotal,
      loading: (s) => s.loading.requests,
      dashboard: (s) => s.dashboard,
    }),

    timezone() {
      return (this.allSettings
        && this.allSettings.GENERAL_TIMEZONE
        && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || '';
    },

    totals() {
      return (this.dashboard && this.dashboard.totals) || {
        requestsSent: 0, clicked: 0, ratedPublic: 0, ratedPrivate: 0,
        optedOutCount: 0, privateFeedbackOpen: 0, sentToday: 0, pendingSend: 0,
      };
    },

    statCards() {
      const t = this.totals;
      return [
        { label: 'Sent today', value: t.sentToday, sub: 'Across SMS & email' },
        { label: 'Google clicks', value: t.clicked, sub: 'Last 30 days' },
        // Replaced "Reviews left" (ratedPublic), which measured nearly the same thing as
        // Google clicks. Open feedback is the only number here that needs someone to DO
        // something, and it used to live on a tab an office had to think to visit.
        { label: 'Private feedback', value: t.privateFeedbackOpen, sub: 'Still open' },
        { label: 'Do not send', value: t.optedOutCount, sub: 'Patients on the list' },
      ];
    },

    pendingSend() {
      return (this.dashboard && this.dashboard.totals && this.dashboard.totals.pendingSend) || 0;
    },

    // "today at 6:00 PM" / "tomorrow at …" / a date — rendered through the SITE timezone,
    // never the browser's. ScheduledFor is stored as site-TZ wall clock, so it is PARSED in
    // that zone rather than converted into it.
    nextSendLabel() {
      const raw = this.dashboard && this.dashboard.nextSendAt;
      if (!raw) return 'shortly';
      const tz = this.timezone || 'America/New_York';
      const when = moment.tz(String(raw).replace('T', ' ').slice(0, 19), 'YYYY-MM-DD HH:mm:ss', tz);
      if (!when.isValid()) return 'shortly';
      const now = moment.tz(tz);
      const time = when.format('h:mm A');
      if (when.isSame(now, 'day')) return `today at ${time}`;
      if (when.isSame(now.clone().add(1, 'day'), 'day')) return `tomorrow at ${time}`;
      return `${when.format('MMM D')} at ${time}`;
    },

    rangeStart() {
      return this.total === 0 ? 0 : this.offset + 1;
    },
    rangeEnd() {
      return Math.min(this.offset + this.pageSize, this.total);
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #0f766e;
$border: #e2e8f0;
$neutral-500: #64748b;
$neutral-400: #94a3b8;
$neutral-900: #0f172a;

.rv-requests { display: flex; flex-direction: column; gap: 16px; }

/* Going-out-next checkpoint. Teal (the product's own colour) rather than amber: these
   sends are the feature working, not a problem to fix. */
.rv-pending-banner {
  display: flex; align-items: center; gap: 13px;
  margin-bottom: 16px; padding: 13px 16px;
  background: #edfbf6;
  border: 1px solid rgba(15, 118, 110, 0.22);
  border-radius: 11px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.rv-pending-banner:hover {
  border-color: rgba(15, 118, 110, 0.45);
  box-shadow: 0 1px 6px rgba(15, 118, 110, 0.1);
}
.rv-pending-icon {
  flex-shrink: 0;
  width: 32px; height: 32px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  background: #fff; color: $primary;
}
.rv-pending-text {
  flex: 1; min-width: 0;
  font-size: 13px; line-height: 1.5; color: $neutral-500;
  b { color: $neutral-900; font-weight: 700; }
}
.rv-pending-btn {
  flex-shrink: 0;
  border: 1px solid rgba(15, 118, 110, 0.35);
  background: #fff; color: $primary;
  font-size: 12.5px; font-weight: 700;
  padding: 7px 14px; border-radius: 8px;
  white-space: nowrap;
}
.rv-pending-btn:hover { background: $primary; color: #fff; border-color: $primary; }

.rv-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.rv-stat-card {
  background: #fff; border: 1px solid $border; border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); padding: 16px 18px;
}
.rv-stat-label { font-size: 12.5px; color: $neutral-500; font-weight: 600; }
.rv-stat-value { font-size: 28px; font-weight: 800; letter-spacing: -0.02em; color: $neutral-900; margin-top: 2px; }
.rv-stat-sub { font-size: 12px; color: $neutral-400; margin-top: 2px; }

.rv-section-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.rv-h2 { font-size: 16px; font-weight: 800; margin: 0; color: $neutral-900; }
.rv-h2-sub { font-size: 12.5px; color: $neutral-500; margin-top: 2px; max-width: 560px; }
.rv-filter { display: flex; align-items: center; gap: 8px; }
.rv-filter-label { font-size: 12px; font-weight: 700; color: $neutral-500; margin: 0; }
.rv-select {
  appearance: none; -webkit-appearance: none;
  border: 1px solid #cbd5e1; border-radius: 9px; background: #fff;
  // See ReviewsSettings.vue: Conversations.vue leaks an unscoped `select { height: 20px }`
  // app-wide, which clips the selected option to nothing. Explicit height + line-height is
  // what keeps the chosen status visible here too.
  height: 36px; line-height: 1.4;
  padding: 7px 30px 7px 12px; font-size: 13px; color: #334155; font-family: inherit; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center;
}

.rv-card { background: #fff; border: 1px solid $border; border-radius: 14px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); }
.rv-table-card { overflow: hidden; }

.rv-table-head, .rv-row {
  display: grid;
  grid-template-columns: 1.5fr 1.6fr 0.9fr 1.5fr 1.1fr 0.9fr;
  gap: 14px;
  padding: 12px 18px;
  align-items: center;
}
.rv-table-head {
  border-bottom: 1px solid $border;
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: $neutral-500;
}
.rv-row {
  border-bottom: 1px solid #f1f5f9;
  &:hover { background: #fafbfb; }
  &.is-off { opacity: 0.55; }
}
.rv-right { text-align: right; justify-content: flex-end; display: flex; }

/* Per-visit Cancel sits beside the permanent opt-out switch. Deliberately understated —
   it is a quiet exception, not the primary action on the row. */
.rv-actions { align-items: center; gap: 10px; }
.rv-cancel-btn {
  border: 1px solid $border; background: #fff; color: $neutral-500;
  font-size: 12px; font-weight: 600;
  padding: 5px 11px; border-radius: 7px; white-space: nowrap;
}
.rv-cancel-btn:hover:not(:disabled) { border-color: #fca5a5; color: #b91c1c; background: #fef2f2; }
.rv-cancel-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.rv-cell { min-width: 0; }

.rv-patient { display: flex; align-items: center; gap: 11px; }
.rv-avatar {
  width: 32px; height: 32px; border-radius: 999px; flex-shrink: 0;
  background: #e2e8f0; color: #475569; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.rv-name { font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: $neutral-900; }
.rv-pid { font-size: 12px; color: $neutral-500; font-family: monospace; }

.rv-appt { font-weight: 600; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rv-appt-sub { font-size: 12px; color: $neutral-500; }

.rv-channel { display: flex; align-items: center; gap: 10px; }
.rv-chan { display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; color: #334155;
  .fa { color: $neutral-500; }
}
.rv-chan-none { color: $neutral-400; }

.rv-status-dot {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 600; color: #334155;
  .rv-dot { width: 8px; height: 8px; border-radius: 999px; background: #94a3b8; }
  &.is-blue .rv-dot { background: #3b82f6; }
  &.is-teal .rv-dot { background: #0d9488; }
  &.is-green .rv-dot { background: #16a34a; }
  &.is-amber .rv-dot { background: #f59e0b; }
  &.is-red .rv-dot { background: #ef4444; }
  &.is-gray .rv-dot { background: #cbd5e1; }
  &.is-neutral .rv-dot { background: #94a3b8; }
}
.rv-status-when { font-size: 11px; color: $neutral-400; margin-top: 2px; }
.rv-skip-reason { font-size: 11.5px; color: $neutral-500; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rv-followup { font-size: 12.5px; color: #475569; }

.rv-table-empty {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  padding: 44px 18px; color: $neutral-500; font-size: 13.5px;
  .fa { font-size: 18px; color: $neutral-400; }
}

/* Custom switch (transparent checkbox on top as click target — house convention) */
.rv-switch { position: relative; display: inline-block; width: 42px; height: 24px; cursor: pointer; margin: 0; }
.rv-switch input { position: absolute; inset: 0; opacity: 0; margin: 0; cursor: pointer; z-index: 2; }
.rv-switch input:disabled { cursor: not-allowed; }
.rv-switch-track {
  position: absolute; inset: 0; background: #cbd5e1; border-radius: 999px; transition: background 0.2s ease;
}
.rv-switch-thumb {
  position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 999px;
  background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); transition: transform 0.2s ease;
}
.rv-switch input:checked ~ .rv-switch-track { background: $primary; }
.rv-switch input:checked ~ .rv-switch-track .rv-switch-thumb { transform: translateX(18px); }

.rv-pager { display: flex; align-items: center; justify-content: center; gap: 16px; }
.rv-pager-btn {
  border: 1px solid #cbd5e1; background: #fff; border-radius: 9px; padding: 7px 14px;
  font-size: 13px; font-weight: 600; color: #334155; cursor: pointer; font-family: inherit;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):hover { background: #f8fafc; }
}
.rv-pager-info { font-size: 12.5px; color: $neutral-500; }

@media (max-width: 991px) {
  .rv-stat-grid { grid-template-columns: repeat(2, 1fr); }
  .rv-table-head { display: none; }
  .rv-row { grid-template-columns: 1fr 1fr; row-gap: 8px; }
  .rv-right { grid-column: 2; }
}
</style>
